import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import type { ReactNode } from 'react';
import { deltaCardProp, formatCompactCurrency, formatPercent } from '../../../lib/format';
import { type BreakdownScope, usePerformanceTab } from '../data/api';
import { OVER_AVG_TIP, OVER_SPEND_TIP, OVER_UPLIFT_TIP } from '../data/constants';
import type { StatCardData } from '../data/mediaBreakdownData';
import type { MediaKpi } from '../data/types';
import { formatKpiValue } from '../data/utils';
import { ErrorState } from '../shared/ErrorState';
import { NoData } from '../shared/NoData';
import { StatCard } from '../shared/StatCard';
import { CreativeTable } from './CreativeTable';
import { WinningMediaElements } from './WinningMediaElements';

export function OverPerforming({
  kpi,
  scope,
  enabled,
}: {
  kpi: MediaKpi;
  scope: BreakdownScope;
  enabled: boolean;
}): ReactNode {
  const { data, isLoading, isError } = usePerformanceTab(scope, 'over', enabled);

  if (isError) return <ErrorState />;

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress size={22} />
      </Box>
    );
  }

  if (!data || data.creatives.length === 0) {
    return <NoData />;
  }

  const symbol = data.currencySymbol;
  const cards: StatCardData[] = [
    {
      label: 'Spend (Over-Performing)',
      value: formatCompactCurrency(data.spend, symbol),
      ...deltaCardProp(data.spendDelta),
      sub: `${formatPercent(data.spendShare)} of total spend`,
      tone: 'primary',
      tip: OVER_SPEND_TIP,
      tipPlacement: 'right',
    },
    {
      label: 'Potential Uplift',
      value: formatKpiValue(data.potentialUplift, kpi, symbol),
      ...deltaCardProp(data.potentialUpliftDelta),
      tone: 'success',
      tip: OVER_UPLIFT_TIP,
    },
    {
      label: `Avg. ${kpi.label}`,
      value: formatKpiValue(data.avgKpi, kpi, symbol),
      ...deltaCardProp(data.avgKpiDelta),
      deltaInvert: !kpi.higherIsBetter,
      tip: OVER_AVG_TIP,
      tipPlacement: 'left',
    },
  ];

  return (
    <Box>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' },
          gap: 3,
          mb: 4,
          '@media print': { gridTemplateColumns: 'repeat(3, 1fr)' },
        }}
      >
        {cards.map((card) => (
          <StatCard key={card.label} card={card} />
        ))}
      </Box>

      <WinningMediaElements elements={data.elements} />

      <CreativeTable
        title="Top Performing Media"
        creatives={data.creatives}
        kpi={kpi}
        currencySymbol={symbol}
        accountIds={scope.accountIds ?? []}
      />
    </Box>
  );
}
